import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { UploadCloud, User, Phone, Mail, CreditCard, MapPin, MessageSquare, CheckCircle2, Plus, X } from "lucide-react";

// Fixed default categories shown for every new order.
// Photograph replaced with Form-16 / Salary Slip per business requirement.
const DOC_TYPES = [
  { key: "PAN Card", label: "PAN Card" },
  { key: "Aadhaar Card", label: "Aadhaar Card" },
  { key: "Bank Statement", label: "Bank Statement" },
  { key: "Form-16 / Salary Slip", label: "Form-16 / Salary Slip" },
  { key: "Other Document", label: "Other Document" },
];

const DEFAULT_SERVICES = [
  "ITR",
  "GST Registration",
  "GST Return Filing",
  "TDS Return Filing",
  "ROC Compliance",
  "Private Limited Company Registration",
  "Audit",
  "CA Certification",
  "Other Services",
];

export default function NewOrder() {
  const nav = useNavigate();
  const [form, setForm] = useState({ client_name: "", phone: "", email: "", pan: "", address: "", remarks: "", service_type: "ITR" });
  // files: { [category]: File }  — includes fixed and custom slots
  const [files, setFiles] = useState({});
  // extraSlots: [{ id, label }]  — dynamic slots added via + button
  const [extraSlots, setExtraSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");
  const [services, setServices] = useState(DEFAULT_SERVICES);

  useEffect(() => {
    api.get("/meta")
      .then((r) => {
        const list = r?.data?.service_types;
        if (Array.isArray(list) && list.length > 0) setServices(list);
      })
      .catch(() => {});
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addExtraSlot = () => {
    const id = `extra_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setExtraSlots((s) => [...s, { id, label: "" }]);
  };

  const updateExtraLabel = (id, label) => {
    setExtraSlots((s) => s.map((x) => (x.id === id ? { ...x, label } : x)));
  };

  const removeExtraSlot = (id) => {
    setExtraSlots((s) => s.filter((x) => x.id !== id));
    setFiles((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  };

  const setExtraFile = (id, file) => {
    setFiles((prev) => ({ ...prev, [id]: file }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    setSubmitting(true);
    try {
      const r = await api.post("/leads", form);
      const leadId = r.data.id;
      const leadNum = r.data.lead_number;

      // Upload fixed-category files
      for (const cat of Object.keys(files)) {
        const f = files[cat];
        if (!f) continue;
        // Resolve display label — for extra slots use user-typed label if any
        let categoryLabel = cat;
        const extra = extraSlots.find((s) => s.id === cat);
        if (extra) categoryLabel = (extra.label || "Additional Document").trim();
        const fd = new FormData();
        fd.append("category", categoryLabel);
        fd.append("file", f);
        await api.post(`/leads/${leadId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setMsg(`Order #${leadNum} created successfully!`);
      setTimeout(() => nav(`/leads/${leadId}`), 800);
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-order-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold">Create New Order</h1>
        <p className="text-sm text-muted-foreground">Enter client details and upload supporting documents.</p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="new-order-form">
        <div className="card p-6 space-y-4">
          <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><User size={18} /> Client Information</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Service Category *</label>
              <select required className="input mt-1" value={form.service_type} onChange={(e) => upd("service_type", e.target.value)} data-testid="input-service-type">
                {services.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Client Name *</label>
              <input required className="input mt-1" value={form.client_name} onChange={(e) => upd("client_name", e.target.value)} data-testid="input-client-name" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Phone size={12} />Phone *</label>
              <input required className="input mt-1" value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="input-phone" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Mail size={12} />Email</label>
              <input type="email" className="input mt-1" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="input-email" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><CreditCard size={12} />PAN Number</label>
              <input className="input mt-1 uppercase" value={form.pan} onChange={(e) => upd("pan", e.target.value.toUpperCase())} data-testid="input-pan" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><MapPin size={12} />Address</label>
              <input className="input mt-1" value={form.address} onChange={(e) => upd("address", e.target.value)} data-testid="input-address" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><MessageSquare size={12} />Details of Client</label>
              <textarea
                className="input mt-1 min-h-[80px]"
                placeholder="Add any relevant details of the client (business type, family info, previous filings, etc.)"
                value={form.remarks}
                onChange={(e) => upd("remarks", e.target.value)}
                data-testid="input-remarks"
              />
            </div>
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-heading font-bold text-lg flex items-center gap-2"><UploadCloud size={18} /> Document Upload</h3>
              <p className="text-xs text-muted-foreground mt-1">Attach documents. All files stored securely for this lead only.</p>
            </div>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={addExtraSlot}
              data-testid="add-more-document-btn"
              title="Add another document slot"
            >
              <Plus size={16} /> Add More Document
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {DOC_TYPES.map((d) => (
              <label key={d.key} className="dropzone block" data-testid={`upload-${d.key.replace(/[\s/]/g, "-")}`}>
                <UploadCloud className="mx-auto mb-2 text-primary" size={24} />
                <div className="font-semibold">{d.label}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {files[d.key] ? <span className="text-emerald-600">✓ {files[d.key].name}</span> : "Click to upload"}
                </div>
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => setFiles((prev) => ({ ...prev, [d.key]: e.target.files[0] }))}
                />
              </label>
            ))}

            {extraSlots.map((slot) => (
              <div
                key={slot.id}
                className="dropzone block relative"
                data-testid={`upload-extra-${slot.id}`}
              >
                <button
                  type="button"
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700 z-10"
                  onClick={() => removeExtraSlot(slot.id)}
                  title="Remove this slot"
                  data-testid={`remove-extra-${slot.id}`}
                >
                  <X size={16} />
                </button>
                <input
                  type="text"
                  className="input text-sm mb-2 w-full text-center"
                  placeholder="Document name (e.g. Rent Agreement)"
                  value={slot.label}
                  onChange={(e) => updateExtraLabel(slot.id, e.target.value)}
                  data-testid={`extra-label-${slot.id}`}
                />
                <label className="cursor-pointer block">
                  <UploadCloud className="mx-auto mb-2 text-primary" size={24} />
                  <div className="text-xs text-muted-foreground mt-1">
                    {files[slot.id] ? (
                      <span className="text-emerald-600">✓ {files[slot.id].name}</span>
                    ) : (
                      "Click to upload"
                    )}
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => setExtraFile(slot.id, e.target.files[0])}
                  />
                </label>
              </div>
            ))}
          </div>
        </div>

        {msg && (
          <div className={`p-3 rounded-lg text-sm ${msg.includes("success") ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900" : "bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900"}`} data-testid="new-order-message">
            {msg.includes("success") && <CheckCircle2 size={16} className="inline mr-2" />}
            {msg}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button type="button" className="btn btn-outline" onClick={() => nav(-1)} data-testid="new-order-cancel-btn">Cancel</button>
          <button type="submit" disabled={submitting} className="btn btn-primary" data-testid="new-order-submit-btn">
            {submitting ? "Submitting…" : "Submit Order"}
          </button>
        </div>
      </form>
    </div>
  );
}
